'use client';
import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { cn } from '@/lib/cn';

export type InputBarState = 'default' | 'editing' | 'readonly' | 'disabled';

export interface InputBarProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /** Top line label, e.g., "Location" */
  label: string;
  placeholder: string;
  /** Current value to render or edit. Controlled from parent. */
  value?: string;
  /** Visual/interaction state */
  state?: InputBarState;
  /** Called when user edits the value (editing state). */
  onChange?: (value: string) => void;
  /** Autofocus the input when in editing state */
  autoFocus?: boolean;
}

export interface InputBarRef {
  focus: () => void;
}

export const InputBar = forwardRef<InputBarRef, InputBarProps>(function InputBar(
  { label, placeholder, value, state = 'default', onChange, autoFocus, className, ...props },
  ref,
) {
  const inputRef = useRef<HTMLInputElement>(null);
  const isDisabled = state === 'disabled';
  const isReadonly = state === 'readonly';

  useImperativeHandle(ref, () => ({
    focus: () => {
      inputRef.current?.focus();
    },
  }));

  useEffect(() => {
    if (autoFocus && state === 'default' && inputRef.current) {
      // Small delay to ensure the panel is open
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [autoFocus, state]);

  return (
    <div
      className={cn(
        // Background is controlled by the parent via `className` (e.g. ListingsSearchBar getInputBg()).
        // Keeping a hardcoded `bg-primary` here can cause a brief "white flash" during state transitions.
        'z-0 bg-transparent rounded-full transition-colors relative',
        'px-6 py-3',
        'hover:z-10',
        'cursor-pointer',
        className,
      )}
      aria-disabled={isDisabled || undefined}
      {...props}
    >
      {/* Content: label + value/input stacked */}
      <div className="min-w-0 flex-1 flex flex-col items-start gap-1">
        {/* Label (top line) */}
        <p className="text-body-2-high text-secondary truncate w-full">{label}</p>

        {/* Value/Input line with persistent placeholder underneath */}
        <input
          ref={inputRef}
          autoFocus={autoFocus}
          placeholder={placeholder}
          value={value ?? ''}
          onChange={e => onChange?.(e.target.value)}
          disabled={isDisabled}
          readOnly={isReadonly}
          aria-label={label}
          className={cn(
            'w-full bg-transparent outline-none',
            'text-body-2-high text-primary',
            'placeholder:text-body-2 placeholder:text-tertiary placeholder:font-normal',
            (isReadonly || isDisabled) && 'cursor-pointer',
          )}
        />
      </div>
    </div>
  );
});

export default InputBar;
